<template>
  <div class="container py-5">
    <div v-if="isLoading" class="text-center m-5">
      <div class="spinner-border mb-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div v-else>
      <template v-if="orderStatus">
        <div class="text-center py-4">
          <h3 class="mb-3">Your order is placed successfully!</h3>
          <p class="text-muted mb-4">
            Take care of your skin with the best products from us. <br />
            You can view your order details and track its status anytime.
          </p>
          <router-link v-if="isLoggedIn" to="/orders" class="btn btn-dark">
            View My Orders
          </router-link>
          <template v-else>
            <p class="mb-3">
              Log in to view your orders and keep track of every purchase.
            </p>
            <router-link to="/login" class="btn btn-dark">
              Log in to view orders
            </router-link>
          </template>
        </div>
      </template>
      <template v-else>
        <h2 class="text-center mb-4">Checkout</h2>
        <div>
          <!-- Billing Details Section -->
          <div class="row">
            <div class="col-lg-6 order-2 order-lg-1">
              <h4>Billing Details</h4>
              <form @submit.prevent="processCheckout">
                <div class="mb-3">
                  <label for="name" class="form-label">Full Name</label>
                  <input
                    type="text"
                    class="form-control"
                    id="name"
                    v-model="checkoutForm.name"
                    maxlength="50"
                    required
                  />
                </div>
                <div class="mb-3">
                  <label for="email" class="form-label">Email</label>
                  <input
                    type="email"
                    class="form-control"
                    id="email"
                    v-model="checkoutForm.email"
                    maxlength="50"
                  />
                </div>
                <div class="mb-3">
                  <label for="phoneNumber" class="form-label"
                    >Phone Number</label
                  >
                  <div v-if="PHONE_VERIFICATION_ENABLED">
                    <input
                      type="text"
                      class="form-control"
                      id="phoneNumber"
                      v-model="checkoutForm.phoneNumber"
                      maxlength="20"
                      required
                      :disabled="!isPhoneVerified"
                    />
                    <button
                      v-if="!isPhoneVerified"
                      class="btn btn-sm btn-outline-danger my-2"
                      @click="startVerification()"
                    >
                      Verify Phone
                    </button>
                  </div>
                  <div v-else class="input-group">
                    <span class="input-group-text" id="basic-addon1">
                      +{{ phoneNumberPrefix }}
                    </span>
                    <input
                      type="text"
                      class="form-control"
                      id="phoneNumber"
                      v-model="checkoutForm.phoneNumber"
                      placeholder="01 XXXX XXXXX"
                      maxlength="15"
                      required
                      @input="onPhoneInput"
                    />
                  </div>
                </div>
                <div class="mb-3">
                  <label for="shippingAddress" class="form-label"
                    >Shipping Address</label
                  >
                  <input
                    type="text"
                    class="form-control"
                    id="shippingAddress"
                    v-model="checkoutForm.shippingAddress"
                    maxlength="200"
                    required
                  />
                </div>
                <div class="mb-3">
                  <label for="citySelection" class="form-label">City</label>
                  <select
                    class="form-select"
                    id="citySelection"
                    v-model="citySelection"
                    @change="onCitySelectionChange"
                    required
                  >
                    <option value="Dhaka">Dhaka</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    v-if="citySelection === 'Other'"
                    type="text"
                    class="form-control mt-2"
                    id="city"
                    v-model="checkoutForm.city"
                    placeholder="Enter your city name"
                    maxlength="20"
                    required
                  />
                </div>
                <div class="mb-3">
                  <label for="zip" class="form-label">Zip Code</label>
                  <input
                    type="text"
                    class="form-control"
                    id="zip"
                    v-model="checkoutForm.zip"
                    maxlength="10"
                  />
                </div>
                <div class="row">
                  <!-- Payment Methods Section -->
                  <div class="col-lg-6">
                    <div class="mt-4">
                      <h4>Payment Method</h4>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="bkash"
                          v-model="checkoutForm.paymentMethod"
                          value="bkash"
                        />
                        <label class="form-check-label" for="bkash">
                          Bkash
                        </label>
                      </div>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="nagad"
                          v-model="checkoutForm.paymentMethod"
                          value="nagad"
                        />
                        <label class="form-check-label" for="nagad">
                          Nagad
                        </label>
                      </div>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="cashOnDelivery"
                          v-model="checkoutForm.paymentMethod"
                          value="cashOnDelivery"
                        />
                        <label class="form-check-label" for="cashOnDelivery">
                          Cash on Delivery
                        </label>
                      </div>
                    </div>
                  </div>
                  <!-- Delivery Location Section -->
                  <div class="col-lg-6">
                    <div class="mt-4">
                      <h4>Delivery Location</h4>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="DeliveryLocation"
                          id="insideDhaka"
                          v-model="checkoutForm.deliveryLocation"
                          value="insideDhaka"
                          disabled
                        />
                        <label class="form-check-label" for="insideDhaka">
                          Inside Dhaka City
                        </label>
                      </div>
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="DeliveryLocation"
                          id="outsideDhaka"
                          v-model="checkoutForm.deliveryLocation"
                          value="outsideDhaka"
                          disabled
                        />
                        <label class="form-check-label" for="outsideDhaka">
                          Outside Dhaka City
                        </label>
                      </div>
                    </div>
                  </div>
                  <!-- Bkash / Nagad TrxID -->
                  <div
                    v-if="
                      checkoutForm.paymentMethod === 'bkash' ||
                      checkoutForm.paymentMethod === 'nagad'
                    "
                    class="row mt-5"
                  >
                    <div class="col-lg-6">
                      <h4>Add Transaction Details</h4>
                      <p>
                        {{ checkoutForm.paymentMethod }} personal number:
                        +8801882124009
                      </p>
                      <p>
                        Please pay {{ subTotal }} BDT and provide these details
                      </p>
                      <div class="mb-3">
                        <label for="trxId" class="form-label">Trx ID</label>
                        <input
                          type="text"
                          class="form-control"
                          id="trxId"
                          v-model="checkoutForm.trxId"
                        />
                      </div>
                      <div class="mb-3">
                        <label for="paymentPhone" class="form-label"
                          >Phone number (payment sent from)</label
                        >
                        <input
                          type="text"
                          class="form-control"
                          id="paymentPhone"
                          v-model="checkoutForm.paymentPhone"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Submit Button -->
                <div class="text-center mt-4">
                  <button class="btn btn-success btn-lg" type="submit">
                    Place Order
                  </button>
                </div>
              </form>
            </div>

            <!-- Order Summary Section -->
            <div class="col-lg-6 order-1 order-lg-2 mb-4 mb-lg-0">
              <h4>Your Order</h4>
              <div class="order-summary table-responsive">
                <table class="table table-bordered align-middle">
                  <thead class="table-light">
                    <tr>
                      <th>Title</th>
                      <th style="min-width: 70px;">Price</th>
                      <th>Qty</th>
                      <th>Total (BDT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in cartItems" :key="item.id">
                      <td>
                        <strong>{{ item.title }}</strong> <br />
                        <small class="opacity-50">
                          <span>
                            {{ item.variant.name }}
                          </span>
                        </small>
                      </td>
                      <td>
                        <span v-if="item.variant.salePrice">
                          <span class="text-decoration-line-through text-muted">
                            ৳ {{ item.variant.price }} <br>
                          </span>
                          <span> ৳ {{ item.variant.salePrice }}</span>
                        </span>
                        <span v-else>
                          ৳ {{ item.variant.price }}
                        </span>
                      </td>
                      <td>{{ item.quantity }}</td>
                      <td> {{ (item.variant.salePrice || item.variant.price) * item.quantity }} </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Summary Section -->
                <div class="border-top pt-3">
                  <div
                    v-if="discount"
                    class="d-flex justify-content-between py-2"
                  >
                    <span>Discount ({{ couponCode }}):</span>
                    <span>- {{ discount }} BDT</span>
                  </div>

                  <!-- Optional payment gateway charge -->
                  <!--
                  <div v-if="paymentMethodCharge" class="d-flex justify-content-between py-2">
                    <span>Payment Gateway Charge:</span>
                    <span>+ {{ paymentMethodCharge }} BDT</span>
                  </div>
                  -->

                  <div class="d-flex justify-content-between py-2">
                    <span>Delivery Charge:</span>
                    <span>+ {{ deliveryCharge }} BDT</span>
                  </div>

                  <div
                    class="d-flex justify-content-between py-2 border-top fw-bold"
                  >
                    <span>Total:</span>
                    <span>{{ subTotal }} BDT</span>
                  </div>
                </div>

                <!-- Add Coupon -->
                <div class="mt-4">
                  <input
                    type="text"
                    class="form-control my-2"
                    name="couponCode"
                    v-model="couponCode"
                    maxlength="30"
                    placeholder="Enter coupon code"
                  />
                  <button
                    class="btn btn-dark"
                    @click="validateCoupon"
                    :disabled="isCouponLoading"
                  >
                    Add Coupon
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </template>
    </div>
    <!-- Login Modal -->
    <div
      class="modal fade"
      id="loginModal"
      tabindex="-1"
      aria-labelledby="loginModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <login-component
              :redirect-page="false"
              :call-from-modal="true"
              @closeModal="closeModal"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Phone Verification Modal -->
    <div
      class="modal fade"
      tabindex="-1"
      :class="{ show: showPhoneModal }"
      style="display: block"
      v-if="showPhoneModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Verify Your Phone Number</h5>
            <button
              type="button"
              class="btn-close"
              @click="closePhoneModal"
            ></button>
          </div>
          <div v-if="addPhoneNumber" class="modal-body">
            <div class="input-group mb-3">
              <span class="input-group-text" id="basic-addon1">
                +{{ phoneNumberPrefix }}
              </span>
              <input
                type="text"
                class="form-control"
                v-model="verificationPhoneNumber"
                placeholder="your phone number"
              />
            </div>
            <button class="btn btn-primary" @click="submitPhoneNumber">
              Send OTP
            </button>
            <p v-if="verificationText" class="text-danger mt-1">
              {{ verificationText }}
            </p>
          </div>
          <div v-else class="modal-body">
            <input
              type="text"
              class="form-control mb-3"
              v-model="verificationCode"
              placeholder="Enter verification code"
            />
            <button class="btn btn-primary" @click="verifyPhone">Submit</button>
            <p v-if="verificationText">{{ verificationText }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  BASE_URL,
  PHONE_VERIFICATION_ENABLED,
} from "@/globals";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min";
import LoginComponent from "./LoginComponent.vue";
import { analytics } from '@/services/analytics';

export default {
  name: "CheckoutComponent",
  components: {
    LoginComponent,
  },
  data() {
    return {
      PHONE_VERIFICATION_ENABLED,
      isLoading: false,
      orderStatus: null,
      isCouponLoading: false,
      errorMessage: null,
      couponCode: null,
      couponInfo: null,
      isPhoneVerified: false,
      showPhoneModal: false,
      verificationCode: null,
      verificationText: "",
      addPhoneNumber: false,
      verificationPhoneNumber: null,
      phoneNumberPrefix: "88",
      citySelection: "Dhaka",
      checkoutForm: {
        name: "",
        phoneNumber: "",
        email: "",
        shippingAddress: "",
        city: "Dhaka",
        zip: "",
        deliveryLocation: "insideDhaka",
        totalPrice: null,
        deliveryCharge: null,
        // paymentMethodCharge: null,
        subTotal: null,
        paymentMethod: "cashOnDelivery",
        trxId: "",
        paymentPhone: "",
      },
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    cartItems() {
      return this.$store.getters.cartItems;
    },
    totalPrice() {
      return this.cartItems.reduce((acc, item) => {
        const price =
          item.variant.salePrice || item.variant.price;
        return acc + price * item.quantity;
      }, 0);
    },
    deliveryCharge() {
      return this.checkoutForm.deliveryLocation === "insideDhaka" ? 80 : 150;
    },
    // paymentMethodCharge() {
    //   if (
    //     this.checkoutForm.paymentMethod === "bkash" ||
    //     this.checkoutForm.paymentMethod === "nagad"
    //   ) {
    //     return Math.ceil((this.totalPrice + this.deliveryCharge) / 50);
    //   } else {
    //     return 0;
    //   }
    // },
    discount() {
      let discountAmount = 0;
      if (this.couponInfo) {
        if (this.couponInfo.discountType === "flat") {
          discountAmount = this.couponInfo.discountValue;
        } else if (this.couponInfo.discountType === "percentage") {
          discountAmount =
            (this.totalPrice * this.couponInfo.discountValue) / 100;
          if (
            this.couponInfo.maxDiscountAmount &&
            discountAmount > this.couponInfo.maxDiscountAmount
          ) {
            discountAmount = this.couponInfo.maxDiscountAmount;
          }
        }
      }

      return discountAmount;
    },
    hardCoverCharge() {
      return this.cartItems.reduce((total, item) => {
        if (item.isHardCover) {
          return total + item.hardcoverExtraCharge * item.quantity;
        }
        return total;
      }, 0);
    },
    subTotal() {
      return Math.ceil(
        this.totalPrice +
          this.deliveryCharge -
          this.discount
      );
    },
  },
  mounted() {
    if (this.cartItems.length === 0) {
      this.$router.push("/");
    }
    this.checkPhoneVerification();
  },
  methods: {
    onCitySelectionChange() {
      if (this.citySelection === "Dhaka") {
        this.checkoutForm.city = "Dhaka";
        this.checkoutForm.deliveryLocation = "insideDhaka";
      } else {
        this.checkoutForm.city = "";
        this.checkoutForm.deliveryLocation = "outsideDhaka";
      }
    },
    async checkout() {
      const token = this.$store.getters.token;

      // Only check phone verification for logged-in users
      if (token && PHONE_VERIFICATION_ENABLED) {
        await this.checkPhoneVerification();
        if (!this.isPhoneVerified) {
          this.errorMessage = "Please verify your phone number";
          this.$root.showToast({
            message: this.errorMessage,
            status: "danger",
            duration: 3000,
          });
          return;
        }
      }
      if (!/^\d{10,15}$/.test(this.checkoutForm.phoneNumber)) {
        this.errorMessage = "Phone number must be 11 digits";
        this.$root.showToast({
          message: this.errorMessage,
          status: "danger",
          duration: 3000,
        });
        return;
      }
      try {
        this.isLoading = true;
        // Only send Authorization header if token exists
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const eventId = crypto.randomUUID()
        const orderData = {
          cart: this.cartItems,
          name: this.checkoutForm.name,
          phoneNumber: this.checkoutForm.phoneNumber,
          email: this.checkoutForm.email,
          shippingAddress: this.checkoutForm.shippingAddress,
          city: this.checkoutForm.city,
          zip: this.checkoutForm.zip,
          deliveryLocation: this.checkoutForm.deliveryLocation,
          deliveryCharge: this.deliveryCharge,
          paymentMethod: this.checkoutForm.paymentMethod,
          trxId: this.checkoutForm.trxId,
          paymentPhone: this.checkoutForm.paymentPhone,
          couponCode: this.couponCode,
          eventId: eventId,
        };
        const response = await axios.post(
          `${BASE_URL}/api/checkout/`,
          orderData,
          { headers }
        );
        if (response.data.status) {
          analytics.purchase(orderData.cart, this.subTotal, eventId)
          this.orderStatus = true;
          this.$store.dispatch("clearCart");
        } else {
          if (response.data?.reason === 'INVALID_TOKEN') {
            this.$store.dispatch("logout");
            setTimeout(() => {
              this.processCheckout()
            }, 300);
            return;
          }
          this.errorMessage = response.data.message;
          this.$root.showToast({
            message: this.errorMessage,
            status: "danger",
            duration: 3000,
          });
        }
      } catch (error) {
        if (error.response?.data?.reason === 'INVALID_TOKEN') {
          this.$store.dispatch("logout");
          setTimeout(() => {
            this.processCheckout()
          }, 300);
        } else {
          this.errorMessage = error.response?.data?.message || "Something went wrong";
          this.$root.showToast({
            message: this.errorMessage,
            status: "danger",
            duration: 3000,
          });
        }
      } finally {
        this.isLoading = false;
        this.scrollToTop();
      }
    },
    processCheckout() {
      // if (!this.isLoggedIn) {
      //   const modalElement = document.getElementById("loginModal");
      //   if (modalElement) {
      //     const modal = Modal.getOrCreateInstance(modalElement);
      //     modal.show();
      //   }
      // } else {
      //   this.checkout();
      // }
      this.checkout();
    },
    closeModal() {
      const modalElement = document.getElementById("loginModal");
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    async validateCoupon() {
      try {
        this.errorMessage = null;
        this.isCouponLoading = true;
        const token = this.$store.getters.token;
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        if (!this.couponCode) {
          this.errorMessage = "Coupon code can't be empty";
          this.$root.showToast({
            message: this.errorMessage,
            status: "danger",
            duration: 3000,
          });
          return;
        }
        const couponData = {
          couponCode: this.couponCode,
          orderAmount: this.totalPrice,
          userLocation: this.checkoutForm.deliveryLocation,
        };
        const response = await axios.post(
          `${BASE_URL}/api/coupons/check_coupon`,
          couponData,
          { headers }
        );
        if (response.data.isApplicable) {
          this.couponInfo = response.data;
        } else {
          this.errorMessage = response.data.message;
        }
      } catch (error) {
        this.couponInfo = null;
        this.errorMessage = error.response?.data?.message || "Something went wrong";
        this.$root.showToast({
          message: this.errorMessage,
          status: "danger",
          duration: 3000,
        });
      } finally {
        this.isCouponLoading = false;
      }
    },
    async checkPhoneVerification() {
      if (PHONE_VERIFICATION_ENABLED) {
        const token = this.$store.getters.token;
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        try {
          const response = await axios.get(
            `${BASE_URL}/api/users/user-phone-verified`,
            { headers }
          );
          if (response.data.status) {
            this.isPhoneVerified = response.data.status;
            this.showPhoneModal = false;
          }
        } catch (error) {
          this.errorMessage = error.response?.data?.message || "Something went wrong";
          this.$root.showToast({
            message: this.errorMessage,
            status: "danger",
            duration: 3000,
          });
        }
      }
    },
    startVerification() {
      this.addPhoneNumber = true;
      this.showPhoneModal = true;
    },
    async submitPhoneNumber() {
      const isValid = /^\d{10}$/.test(this.verificationPhoneNumber);

      if (!isValid) {
        this.verificationText = "Phone number must be exactly 10 digits";
        return;
      }

      try {
        const token = this.$store.getters.token;
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.post(
          `${BASE_URL}/api/users/otp-generate`,
          {
            phoneNumber: `${this.phoneNumberPrefix}${this.verificationPhoneNumber}`,
          },
          { headers }
        );
        if (response.data.status) {
          this.addPhoneNumber = false;
        }
      } catch (error) {
        this.verificationText = "Verification failed. Please try again.";
      }
    },
    async verifyPhone() {
      try {
        const token = this.$store.getters.token;
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.post(
          `${BASE_URL}/api/users/verify-phone-number`,
          { otp: this.verificationCode },
          { headers }
        );

        if (response.data.status) {
          this.verificationText = response.data.message;
          this.isPhoneVerified = true;
          setTimeout(() => {
            this.closePhoneModal();
            this.verificationText = "";
          }, 5000);
        } else {
          this.verificationText = "OTP verification failed. Please try again.";
        }
      } catch (error) {
        this.verificationText = "OTP verification failed. Please try again.";
      }
    },
    closePhoneModal() {
      this.showPhoneModal = false;
      this.verificationCode = null;
    },
    onPhoneInput(e) {
      // Remove non-numeric characters
      let digits = e.target.value.replace(/\D/g, "");

      // Limit to 15 digits
      if (digits.length > 15) {
        digits = digits.slice(0, 15);
      }

      this.checkoutForm.phoneNumber = digits;
    },
  },
};
</script>

<style scoped>
.order-summary {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 5px;
}

.btn-lg {
  width: 100%;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
.modal.show {
  display: block;
}
</style>
